/**
 * Phase B2 — TimePresetSelect
 *
 * Composant réutilisable de saisie d'heure sur mobile/desktop :
 * - Select shadcn avec presets 7:00 → 19:00 par pas de 15 min (49 options)
 * - Option "Autre…" bascule sur `<Input type="time">` en fallback
 *
 * Utilisé dans le form RDV (ProjectDetailPage) et création tâche (TasksBlock)
 * pour éviter la saisie clavier au tap sur mobile.
 *
 * Props :
 *   value    : "HH:MM" (24h) — chaîne vide autorisée
 *   onChange : (str) => void — reçoit "HH:MM"
 *   testid   : préfixe des data-testid (`${testid}-select`, `${testid}-custom`)
 *   className: classes appliquées au SelectTrigger racine
 */
import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

const CUSTOM = "__custom__";

// Presets 7:00 → 19:00 pas 15 min = 49 valeurs (49 = 12 * 4 + 1)
const PRESETS = (() => {
  const out = [];
  for (let h = 7; h <= 19; h++) {
    for (const m of [0, 15, 30, 45]) {
      if (h === 19 && m > 0) break;
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      out.push(`${hh}:${mm}`);
    }
  }
  return out;
})();

export default function TimePresetSelect({
  value,
  onChange,
  testid = "time-preset",
  className = "",
}) {
  const isPreset = useMemo(() => value && PRESETS.includes(value), [value]);
  const selectValue = value === "" || isPreset ? value : CUSTOM;

  return (
    <div className="flex items-center gap-2" data-testid={`${testid}-wrapper`}>
      <Select
        value={selectValue || undefined}
        onValueChange={(v) => {
          if (v === CUSTOM) {
            // Bascule vers saisie libre — préserve la valeur courante (elle
            // deviendra visible dans l'input fallback qui apparaît à côté).
            if (isPreset || !value) onChange(value || "09:00");
            return;
          }
          onChange(v);
        }}
      >
        <SelectTrigger
          className={`h-10 w-28 border-slate-700 bg-slate-950/40 ${className}`}
          data-testid={`${testid}-select`}
        >
          <SelectValue placeholder="—" />
        </SelectTrigger>
        <SelectContent className="max-h-72 border-slate-700 bg-slate-900">
          {PRESETS.map((p) => (
            <SelectItem key={p} value={p}>
              {p}
            </SelectItem>
          ))}
          <SelectItem value={CUSTOM}>Autre…</SelectItem>
        </SelectContent>
      </Select>
      {!isPreset && value && (
        <Input
          type="time"
          step={60}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="h-10 w-28 border-slate-700 bg-slate-950/40"
          data-testid={`${testid}-custom`}
        />
      )}
    </div>
  );
}
